import React, { useState } from "react";
import { Link, type To } from "react-router-dom";
import {
	Button,
	Menu,
	MenuItem,
	useTheme,
	Typography,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
// TEMP: plain logbook navbar (no Sections page) until the Sections feature is decided
// with Roger. Swap back to `sectionLogbookNavbar` to re-enable sections.
import { logbookNavbar, useHasPermission } from "@dexteel/mesf-core";


interface MenuItemConfig {
	label: string;
	to: To;
	permission?: string;
}

interface MenuConfig {
	id: string;
	label: string;
	items: MenuItemConfig[];
	permission?: string;
}

const menuConfigData: MenuConfig[] = [
	{
		id: "map",
		label: "Map",
		items: [
			{
				label: "Map",
				to: "/map",
			},
		],
	},
	{
		id: "work-orders",
		label: "Work Orders",
		items: [
			{
				label: "Work Orders",
				to: "/work-orders",
			},
		],
	},
	{
		id: "task-list",
		label: "Task List",
		items: [
			{
				label: "Task List",
				to: "/task-list/downday",
			},
		],
	},
	{
		id: "templates",
		label: "Templates",
		items: [
			{
				label: "Templates",
				to: "/templates",
			},
		],
	},
	...logbookNavbar,
];

const CustomNavbar = () => {
	const hasPermissionTo = useHasPermission();
	const theme = useTheme();
	const [anchorElements, setAnchorElements] = useState<{
		[key: string]: HTMLElement | null;
	}>({});

	const handleMenuOpen = (
		event: React.MouseEvent<HTMLButtonElement>,
		menuId: string,
	) => {
		setAnchorElements((prevAnchors) => ({
			...prevAnchors,
			[menuId]: event.currentTarget,
		}));
	};

	const handleMenuClose = (menuId: string) => {
		setAnchorElements((prevAnchors) => ({
			...prevAnchors,
			[menuId]: null,
		}));
	};

	return (
		<>
			{menuConfigData.map((menu) => {
				if (menu.permission && !hasPermissionTo(menu.permission)) {
					return null;
				}

				const currentAnchorEl = anchorElements[menu.id] || null;
				const isMenuOpen = Boolean(currentAnchorEl);

				if (menu.items.length === 1) {
					const singleItem = menu.items[0];

					if (
						singleItem.permission &&
						!hasPermissionTo(singleItem.permission)
					) {
						return null;
					}

					return (
						<Button
							key={menu.id}
							component={Link}
							to={singleItem.to}
							style={{ marginRight: theme.spacing(2), color: "white", display: "flex", alignItems: "center" }}>
							{menu.label}
						</Button>
					);
				}

				return (
					<React.Fragment key={menu.id}>
						<Button
							aria-controls={`${menu.id}-menu`}
							aria-haspopup="true"
							onClick={(event) => handleMenuOpen(event, menu.id)}
							style={{ marginRight: theme.spacing(2), color: "white", display: "flex", alignItems: "center" }}>
							{menu.label}
							{isMenuOpen ? (
								<ExpandLess style={{ marginLeft: theme.spacing(0.5) }} />
							) : (
								<ExpandMore style={{ marginLeft: theme.spacing(0.5) }} />
							)}
						</Button>
						<Menu
							id={`${menu.id}-menu`}
							anchorEl={currentAnchorEl}
							keepMounted
							open={isMenuOpen}
							onClose={() => handleMenuClose(menu.id)}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "center",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "center",
							}}>
							{menu.items
								.filter(
									(item) =>
										!item.permission || hasPermissionTo(item.permission),
								)
								.map((item, index) => (
									<MenuItem
										key={`${menu.id}-item-${index}`}
										component={Link}
										to={item.to}
										onClick={() => handleMenuClose(menu.id)}
										style={{ textDecoration: "none", color: "inherit", display: "block", width: "100%" }}>
										<Typography>
											{item.label}
										</Typography>
									</MenuItem>
								))}
						</Menu>
					</React.Fragment>
				);
			})}
		</>
	);
};

export { CustomNavbar };
